import { lazy, Suspense } from "react";
import { ArrowRight, FileCode2, GitPullRequest } from "lucide-react";
import type { ReviewGuide } from "../../shared/guide";
import type { FixtureDescriptor, FixtureFiles } from "../lib/fixtures";
import { deriveCodeBreakdown, type DerivedCodeFile } from "../lib/code-breakdown";
import type { ChewCodeTarget } from "./ChewMarkdown";
import { GuideProse } from "./GuideProse";
import { repositoryShortName } from "../lib/pull-view";

const ChewMarkdown = lazy(() => import("./ChewMarkdown").then((module) => ({ default: module.ChewMarkdown })));

interface OverviewCardProps {
  description?: string;
  descriptor: FixtureDescriptor;
  files: FixtureFiles;
  guide: ReviewGuide;
  /** Whether the guide carries an authored narrative worth briefing from. */
  guided?: boolean;
  onSelectCode: (target: ChewCodeTarget) => void;
  onStart: () => void;
  resuming: boolean;
  selectableCodePaths: ReadonlySet<string>;
}

function fileChangeLabel(file: DerivedCodeFile) {
  if (file.additions !== undefined && file.deletions !== undefined) return `+${file.additions} −${file.deletions}`;
  if (file.changedLines !== undefined) return `${file.changedLines.toLocaleString()} changed`;
  return file.status.replace("-", " ");
}

export function OverviewCard({ description, descriptor, files, guide, guided = false, onSelectCode, onStart, resuming, selectableCodePaths }: OverviewCardProps) {
  const breakdown = deriveCodeBreakdown({
    changedFiles: guide.coverage.files,
    manifest: files.manifest,
    snapshots: files.files,
  });
  const body = description?.trim();
  const codePaths = breakdown.files.map((file) => file.path).filter((path) => selectableCodePaths.has(path));
  const stack = guide.scope.stack;
  const overview = guide.overview;
  const prose = (text: string) =>
    <GuideProse codePaths={codePaths} onSelectCode={onSelectCode} repository={descriptor.repository} text={text} />;
  // The authored narrative used to live only behind the ⓘ dialog, one click
  // away from the guided flow it exists for.
  const briefing = guided && Boolean(overview.lead || overview.problem || overview.approach || overview.reviewFocus.length > 0);

  return (
    <section className="intro-page pr-briefing">
      <header className="pr-briefing-heading">
        <div>
          <span><GitPullRequest aria-hidden="true" />{repositoryShortName(descriptor.repository)} #{descriptor.pullNumber}</span>
          <h1>{descriptor.title}</h1>
          <div className="pr-briefing-facts">
            <span>{descriptor.stats.changedFiles} {descriptor.stats.changedFiles === 1 ? "file" : "files"}</span>
            <span className="addition">+{descriptor.stats.additions.toLocaleString()}</span>
            <span className="deletion">−{descriptor.stats.deletions.toLocaleString()}</span>
            <span>{descriptor.stats.commits} {descriptor.stats.commits === 1 ? "commit" : "commits"}</span>
            {stack.role !== "standalone" && <span>Stack {stack.position}/{stack.total}</span>}
          </div>
        </div>
        <button className="primary-button pr-briefing-start" onClick={onStart} type="button">
          {resuming ? "Continue review" : "Start code review"}<ArrowRight aria-hidden="true" />
        </button>
      </header>

      {briefing && (
        <article className="pr-briefing-guide">
          <header>
            <span>Review briefing</span>
            {overview.title.trim() !== descriptor.title.trim() && <h2>{overview.title}</h2>}
          </header>
          {overview.lead && <p className="pr-briefing-lead">{prose(overview.lead)}</p>}
          {(overview.problem || overview.approach) && (
            <div className="pr-briefing-pair">
              {overview.problem && <div><strong>Problem</strong><p>{prose(overview.problem)}</p></div>}
              {overview.approach && <div><strong>Approach</strong><p>{prose(overview.approach)}</p></div>}
            </div>
          )}
          <div className="pr-briefing-risk">
            <span className={`pr-briefing-risk-badge risk-${overview.riskLevel}`}>{overview.riskLevel} risk</span>
            <p>{prose(overview.mainRisk)}</p>
          </div>
          {overview.reviewFocus.length > 0 && (
            <div className="pr-briefing-focus">
              <strong>Focus your review on</strong>
              <ul>{overview.reviewFocus.map((focus) => <li key={focus}>{prose(focus)}</li>)}</ul>
            </div>
          )}
        </article>
      )}

      <div className="pr-briefing-layout">
        <article className="pr-briefing-description">
          <header><div><span>PR description</span><h2>Context from the author</h2></div>{body && <small>{body.length.toLocaleString()} characters</small>}</header>
          {body ? (
            <Suspense fallback={<div className="markdown-loading">Formatting description…</div>}>
              <ChewMarkdown body={body} codePaths={codePaths} onSelectCode={onSelectCode} repository={descriptor.repository} showTableOfContents />
            </Suspense>
          ) : (
            <div className="pr-description-empty">
              <strong>No description was provided.</strong>
              <p>The diff-derived breakdown still gives you a quick map before opening the code.</p>
            </div>
          )}
        </article>

        <aside className="code-breakdown">
          <header>
            <div><span>Code breakdown</span><h2>What the diff contains</h2></div>
            <small>Derived from {breakdown.source === "git-manifest" ? "the exact diff" : "changed paths"}</small>
          </header>
          <div className="code-breakdown-total">
            <strong>{breakdown.files.length}</strong><span>changed {breakdown.files.length === 1 ? "file" : "files"}</span>
            <em>+{descriptor.stats.additions.toLocaleString()} −{descriptor.stats.deletions.toLocaleString()}</em>
          </div>
          <ol>
            {breakdown.areas.map((area) => (
              <li key={area.id}>
                <header><strong>{area.label}</strong><span>{area.files.length} {area.files.length === 1 ? "file" : "files"}{area.changedLines !== undefined ? ` · ${area.changedLines.toLocaleString()} lines` : ""}</span></header>
                <div>
                  {area.files.slice(0, 3).map((file) => (
                    <button
                      disabled={!selectableCodePaths.has(file.path)}
                      key={file.path}
                      onClick={() => onSelectCode({ path: file.path })}
                      title={selectableCodePaths.has(file.path) ? `Open ${file.path} in the review` : `${file.path} is not part of a guided code stop`}
                      type="button"
                    >
                      <FileCode2 aria-hidden="true" /><span><strong>{file.path.split("/").at(-1)}</strong><small>{file.path}</small></span><em>{fileChangeLabel(file)}</em>
                    </button>
                  ))}
                  {area.files.length > 3 && <p>+{area.files.length - 3} more in this area</p>}
                </div>
              </li>
            ))}
          </ol>
          <footer>Computed locally from filenames, statuses, and line counts. Guide annotations are not used.</footer>
        </aside>
      </div>
    </section>
  );
}
